import { useEffect } from "react";
import Lenis from "@studio-freight/lenis";
import { HeroSectionAnimated } from "../components/HeroSectionAnimated";
import { motion, useScroll, useTransform } from "framer-motion";
import { Paintbrush, Car, Sparkles, Shield, Droplets, Wrench } from "lucide-react";

// Services data
const SERVICES = [
  { icon: Paintbrush, title: "Full Resprays", desc: "Complete colour transformations with factory-quality finish" },
  { icon: Sparkles, title: "Custom Paint", desc: "Unique designs, pearl effects, and show-quality finishes" },
  { icon: Car, title: "Colour Matching", desc: "Precise matching for seamless panel repairs" },
  { icon: Wrench, title: "Dent & Scratch", desc: "Expert bodywork and paint correction" },
  { icon: Shield, title: "Ceramic Coating", desc: "Long-lasting protection with incredible shine" },
  { icon: Droplets, title: "Detailing", desc: "Interior and exterior restoration to showroom condition" },
];

// Portfolio images - your actual builds
const PORTFOLIO_IMAGES = [
  { url: "https://customer-assets-jai6qajn.emergentagent.net/job_1406887e-27f1-4dc9-a01d-472f5d3042b6/artifacts/3occ3nlq_IMG_0448.jpeg", title: "Black GC8 WRX STI" },
  { url: "https://customer-assets-jai6qajn.emergentagent.net/job_1406887e-27f1-4dc9-a01d-472f5d3042b6/artifacts/tr6mfqet_IMG_0458.jpeg", title: "Blue Mazda RX-7 FD" },
  { url: "https://customer-assets-jai6qajn.emergentagent.net/job_1406887e-27f1-4dc9-a01d-472f5d3042b6/artifacts/dlbr9rqm_IMG_0450.jpeg", title: "Grey Blobeye WRX" },
  { url: "https://customer-assets-jai6qajn.emergentagent.net/job_1406887e-27f1-4dc9-a01d-472f5d3042b6/artifacts/jqyyjfzm_IMG_8372.jpeg", title: "White R34 Skyline" },
];

export default function AnimatedLandingDemo() {
  // Initialize Lenis smooth scroll
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      smoothWheel: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => lenis.destroy();
  }, []);

  return (
    <div className="bg-[#050505] min-h-screen">
      {/* Demo Banner */}
      <div className="fixed top-0 left-0 right-0 z-[100] bg-[#3b82f6] text-white text-center py-2 text-sm font-body">
        🎨 ANIMATION DEMO - Toggle hero options on the right. Scroll to see animations!
      </div>

      {/* Animated Hero */}
      <HeroSectionAnimated heroOption="gc8" />

      {/* Services Section with Scroll Animation */}
      <section className="py-24 px-6 md:px-12 max-w-[1400px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-heading text-4xl md:text-5xl uppercase text-white mb-4">Our Services</h2>
          <p className="text-zinc-500 font-body max-w-xl mx-auto">Premium automotive refinishing for JDM enthusiasts and car lovers alike.</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((service, i) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              whileHover={{ y: -5, borderColor: "rgba(59, 130, 246, 0.5)" }}
              className="border border-zinc-800 bg-zinc-900/50 p-6 transition-all duration-300 group cursor-pointer"
            >
              <service.icon className="w-8 h-8 text-[#3b82f6] mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="font-heading text-xl uppercase text-white mb-2">{service.title}</h3>
              <p className="text-zinc-500 font-body text-sm">{service.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Portfolio Horizontal Scroll Section */}
      <section className="py-24 overflow-hidden">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 px-6"
        >
          <h2 className="font-heading text-4xl md:text-5xl uppercase text-white mb-4">Featured Builds</h2>
          <p className="text-zinc-500 font-body">Some of our recent work. JDM legends brought back to life.</p>
        </motion.div>

        <div className="flex gap-6 px-6 overflow-x-auto pb-8 snap-x snap-mandatory scrollbar-hide">
          {PORTFOLIO_IMAGES.map((img, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              whileHover={{ scale: 1.02 }}
              className="flex-shrink-0 w-[80vw] md:w-[50vw] lg:w-[35vw] aspect-[4/3] relative overflow-hidden snap-center group"
            >
              <img
                src={img.url}
                alt={img.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                <h3 className="font-heading text-xl uppercase text-white">{img.title}</h3>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-heading text-4xl md:text-6xl uppercase text-white mb-6">
            Ready for your <span className="text-[#3b82f6]">transformation?</span>
          </h2>
          <p className="text-zinc-500 font-body mb-8 max-w-xl mx-auto">
            Get a free quote and join our portfolio of happy customers.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-white text-black font-heading text-lg tracking-[0.15em] uppercase px-12 py-4 cursor-pointer border-none hover:bg-[#3b82f6] hover:text-white transition-colors"
          >
            Get a Quote
          </motion.button>
        </motion.div>
      </section>

      {/* Footer spacer */}
      <div className="h-32" />
    </div>
  );
}
